import Vue from 'vue';
import { LOCALSTORAGE_IDS } from '../data/constants';
import { getItem, setItem } from './storage';
import { clearNonEssentialCookies, clearNonEssentialStorage } from './cookies';

// Bump this when the purposes described in the banner change, so that everyone
// is asked again rather than silently carried over.
export const CONSENT_VERSION = 1;

// Consent is not open ended. EDPB/ICO guidance expects it to be refreshed;
// twelve months is the usual interval.
export const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

// This project has no Vuex/Pinia store, so a single observable stands in for
// one. Components read `consentState.allowCookies` in a computed and therefore
// react when the decision changes, instead of reading localStorage once on
// mount and being stuck with a stale answer for their whole lifetime.
export const consentState = Vue.observable({
  allowCookies: false,
  hasDecided: false,
});

/**
 * The stored decision, or null when there is none to honour.
 * @returns {boolean|null}
 */
export function readConsent() {
  const raw = getItem(LOCALSTORAGE_IDS.USER_COOKIES);
  if (raw === null) {
    return null;
  }

  let record;
  try {
    record = JSON.parse(raw);
  } catch (error) {
    return null;
  }

  // The flag used to be a bare `true`/`false`. Treat any non-versioned value as
  // undecided so existing visitors re-consent once under the new record.
  if (typeof record !== 'object' || record === null) {
    return null;
  }
  if (record.v !== CONSENT_VERSION || typeof record.ts !== 'number') {
    return null;
  }
  if (Date.now() - record.ts > CONSENT_MAX_AGE_MS) {
    return null;
  }

  return Boolean(record.allowed);
}

export function writeConsent(allowed) {
  setItem(
    LOCALSTORAGE_IDS.USER_COOKIES,
    JSON.stringify({ v: CONSENT_VERSION, allowed, ts: Date.now() })
  );
}

// For callers that are not components and cannot use a computed.
export function hasAnalyticsConsent() {
  return consentState.allowCookies;
}

/**
 * Loads the stored decision into `consentState`. Safe to call before mount.
 * @returns {boolean|null} the decision that was found, if any
 */
export function initConsent() {
  const decision = readConsent();
  consentState.hasDecided = decision !== null;
  consentState.allowCookies = decision === true;
  return decision;
}

/**
 * Records a decision. On a decline this also wipes every non-essential cookie
 * and storage key, which covers the case where cookies outlived the consent
 * record and the visitor is declining with stale identifiers still set.
 */
export function setConsent(allowed) {
  writeConsent(allowed);
  consentState.allowCookies = allowed;
  consentState.hasDecided = true;

  if (!allowed) {
    clearNonEssentialCookies();
    clearNonEssentialStorage();
  }
}

// Brings the banner back so a decision can be changed at any time: withdrawing
// consent has to be as easy as giving it.
export function reopenConsent() {
  consentState.hasDecided = false;
}
